import React, { useEffect, useMemo, useState } from 'react';
import {
  Mail, FileSpreadsheet, FileArchive, AlertCircle, CheckCircle2, Play, Eye, Trash2, Plus, Loader2, Building2, X, RefreshCw, ShieldAlert,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useFetch, Spinner } from '../lib/useFetch';
import { notificationsApi, reportsApi, companiesApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const TYPE_LABELS = {
  formazione: 'Formazione',
  visite: 'Visite mediche',
  dpi: 'DPI',
  haccp: 'HACCP',
  sicurezza: 'Sicurezza',
};

const FREQ_LABELS = { daily: 'Ogni giorno', weekly: 'Ogni settimana', monthly: 'Ogni mese' };
const DOW = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

const SectionCard = ({ icon: Icon, color, bg, title, subtitle, children, action }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
    <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: bg, color }}>
          <Icon size={20} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800 leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const ReportisticaPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  return (
    <div className="space-y-5" data-testid="reportistica-page">
      <Tabs defaultValue={isClient ? 'export' : 'email'} className="w-full">
        <TabsList className="bg-slate-100 p-1 flex-wrap h-auto">
          {!isClient && (
            <TabsTrigger value="email" data-testid="tab-email" className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
              <Mail size={14} /> Notifiche Email
            </TabsTrigger>
          )}
          <TabsTrigger value="export" data-testid="tab-export-company" className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
            <FileArchive size={14} /> Export per Ispezione
          </TabsTrigger>
          <TabsTrigger value="bulk" data-testid="tab-export-bulk" className="data-[state=active]:bg-[#023F80] data-[state=active]:text-white text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold flex items-center gap-2">
            <FileSpreadsheet size={14} /> Export Massivo
          </TabsTrigger>
        </TabsList>
        {!isClient && (
          <TabsContent value="email" className="mt-5">
            <NotificheTab />
          </TabsContent>
        )}
        <TabsContent value="export" className="mt-5">
          <ExportCompanyTab />
        </TabsContent>
        <TabsContent value="bulk" className="mt-5">
          <ExportBulkTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============================================================
// Notifiche tab
// ============================================================
export const NotificheTab = () => {
  const { data: config, reload: reloadCfg } = useFetch(() => notificationsApi.config(), []);
  const { data: rules, loading, reload } = useFetch(() => notificationsApi.listRules(), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const onEdit = (rule) => { setEditing(rule); setModalOpen(true); };
  const onCreate = () => { setEditing(null); setModalOpen(true); };

  const onDelete = async (rule) => {
    if (!window.confirm(`Eliminare la regola "${rule.name}"?`)) return;
    try {
      await notificationsApi.deleteRule(rule.id);
      toast.success('Regola eliminata');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const onRun = async (rule) => {
    const t = toast.loading('Invio in corso...');
    try {
      const res = await notificationsApi.runRule(rule.id);
      toast.dismiss(t);
      const status = res.data?.result?.status;
      if (status === 'sent') toast.success(`Email inviata (${res.data.items} scadenze)`);
      else if (status === 'mocked') toast.warning(`SMTP non configurato — invio simulato (${res.data.items} scadenze)`);
      else toast.error(`Errore invio: ${res.data?.result?.error || 'sconosciuto'}`);
      reload();
    } catch (e) {
      toast.dismiss(t);
      toast.error(e.response?.data?.detail || 'Errore esecuzione');
    }
  };

  return (
    <div className="space-y-5">
      {/* Stato SMTP */}
      {config && (
        <div data-testid="smtp-status-card" className={`rounded-2xl p-4 border ${config.smtp_configured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-start gap-3 flex-wrap">
            {config.smtp_configured ? <CheckCircle2 size={18} className="text-green-700 mt-0.5" /> : <AlertCircle size={18} className="text-amber-700 mt-0.5" />}
            <div className="text-sm flex-1 min-w-0">
              <div className="font-semibold text-gray-800">
                {config.smtp_configured ? 'Server SMTP configurato' : 'Server SMTP non configurato'}
              </div>
              <div className="text-xs text-slate-600 mt-1 break-all">
                Mittente: <code className="bg-white/60 px-1.5 py-0.5 rounded">{config.sender}</code>
                {config.smtp_host && <> · Server: <code className="bg-white/60 px-1.5 py-0.5 rounded">{config.smtp_host}:{config.smtp_port}</code></>}
              </div>
              {!config.smtp_configured && (
                <div className="text-xs text-amber-800 mt-2 leading-relaxed">
                  Imposta <code>SMTP_HOST</code>, <code>SMTP_USER</code>, <code>SMTP_PASSWORD</code> in <code>/app/backend/.env</code> per attivare l'invio reale.
                </div>
              )}
            </div>
            <div className="flex gap-2 ml-auto">
              <button onClick={async () => {
                const to = window.prompt('Indirizzo destinatario per email di prova:', config.sender?.match(/<(.+)>/)?.[1] || '');
                if (!to) return;
                const t = toast.loading('Invio email di prova...');
                try {
                  const res = await notificationsApi.testSmtp(to);
                  toast.dismiss(t);
                  if (res.data.status === 'sent') toast.success(`Email inviata a ${to}`);
                  else if (res.data.status === 'mocked') toast.warning('SMTP non configurato — invio simulato');
                  else toast.error(`Errore: ${res.data.error || 'sconosciuto'}`);
                } catch (e) { toast.dismiss(t); toast.error(e.response?.data?.detail || 'Errore'); }
              }} data-testid="test-smtp-btn" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold">
                Email di prova
              </button>
              <button onClick={reloadCfg} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><RefreshCw size={12} /></button>
            </div>
          </div>
        </div>
      )}

      <SectionCard
        icon={Mail}
        color="#023F80"
        bg="#e0e7ff"
        title="Regole di notifica scadenze"
        subtitle="Email automatiche programmate per avvisare in anticipo le scadenze di formazione, visite, DPI, HACCP e sicurezza."
        action={
          <button onClick={onCreate} data-testid="new-rule-btn" className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2">
            <Plus size={14} /> NUOVA REGOLA
          </button>
        }
      >
        {loading ? <Spinner /> : (
          (rules || []).length === 0 ? (
            <div className="py-14 text-center" data-testid="rules-empty">
              <Mail size={36} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Nessuna regola attiva. Crea la prima per ricevere notifiche automatiche.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="rules-list">
              {(rules || []).map((rule) => <RuleCard key={rule.id} rule={rule} companies={companies || []} onEdit={onEdit} onDelete={onDelete} onRun={onRun} />)}
            </div>
          )
        )}
      </SectionCard>

      <RuleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        rule={editing}
        companies={companies || []}
        config={config}
        onSaved={() => { setModalOpen(false); reload(); }}
      />
    </div>
  );
};

const RuleCard = ({ rule, companies, onEdit, onDelete, onRun }) => {
  const scope = rule.companyIds?.[0] === '*'
    ? 'Tutte le aziende'
    : `${rule.companyIds.length} aziend${rule.companyIds.length === 1 ? 'a' : 'e'}`;
  const types = (rule.types || []).map((t) => TYPE_LABELS[t] || t).join(', ');
  const recipients = (rule.recipients || []).join(', ');
  const schedule = rule.frequency === 'daily'
    ? `Ogni giorno alle ${String(rule.hourUtc).padStart(2,'0')}:05 UTC`
    : rule.frequency === 'weekly'
      ? `${DOW[rule.dayOfWeek]} alle ${String(rule.hourUtc).padStart(2,'0')}:05 UTC`
      : `Il ${rule.dayOfMonth} del mese alle ${String(rule.hourUtc).padStart(2,'0')}:05 UTC`;

  return (
    <div className="border border-slate-200 rounded-xl p-4 hover:border-[#023F80]/40 transition-colors" data-testid={`rule-${rule.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h3 className="font-bold text-gray-800">{rule.name}</h3>
            {rule.active
              ? <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-[10px] font-bold uppercase">Attiva</span>
              : <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-[10px] font-bold uppercase">Sospesa</span>
            }
            <span className="px-2 py-0.5 bg-[#fef7d4] text-[#8a6608] rounded-full text-[10px] font-bold">Preavviso {rule.advanceDays}gg</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-slate-600">
            <div><span className="text-slate-400">Tipi:</span> <span className="text-slate-700 font-medium">{types || '—'}</span></div>
            <div><span className="text-slate-400">Ambito:</span> <span className="text-slate-700 font-medium">{scope}</span></div>
            <div><span className="text-slate-400">Frequenza:</span> <span className="text-slate-700 font-medium">{schedule}</span></div>
            <div><span className="text-slate-400">Destinatari:</span> <span className="text-slate-700 font-medium break-all">{recipients}</span></div>
            {rule.lastRunAt && (
              <div className="md:col-span-2"><span className="text-slate-400">Ultimo invio:</span> <span className="text-slate-700">{new Date(rule.lastRunAt).toLocaleString('it-IT')} — {rule.lastRunStatus} ({rule.lastRunCount} record)</span></div>
            )}
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onRun(rule)} data-testid={`run-${rule.id}`} title="Esegui ora" className="p-2 rounded-md bg-[#023F80] text-white hover:bg-[#1e293b]"><Play size={14} /></button>
          <button onClick={() => onEdit(rule)} title="Modifica" className="p-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700"><Eye size={14} /></button>
          <button onClick={() => onDelete(rule)} title="Elimina" className="p-2 rounded-md bg-red-50 hover:bg-red-100 text-red-700"><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
};

const emptyRule = {
  name: '',
  recipients: [''],
  companyIds: ['*'],
  types: ['formazione', 'visite'],
  advanceDays: 60,
  frequency: 'weekly',
  dayOfWeek: 0,
  dayOfMonth: 1,
  hourUtc: 6,
  active: true,
};

const RuleModal = ({ open, onClose, rule, companies, onSaved }) => {
  const [form, setForm] = useState(emptyRule);
  const [saving, setSaving] = useState(false);
  const [previewItems, setPreviewItems] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(rule ? { ...emptyRule, ...rule, recipients: rule.recipients?.length ? rule.recipients : [''] } : emptyRule);
      setPreviewItems(null);
    }
  }, [open, rule]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleType = (t) => {
    const types = form.types.includes(t) ? form.types.filter((x) => x !== t) : [...form.types, t];
    set('types', types);
  };

  const allCompanies = form.companyIds?.[0] === '*';
  const setAllCompanies = (yes) => set('companyIds', yes ? ['*'] : []);
  const toggleCompany = (cid) => {
    if (allCompanies) {
      set('companyIds', [cid]);
      return;
    }
    set('companyIds', form.companyIds.includes(cid) ? form.companyIds.filter((x) => x !== cid) : [...form.companyIds, cid]);
  };

  const updRecipient = (i, val) => {
    const next = [...form.recipients];
    next[i] = val;
    set('recipients', next);
  };
  const addRecipient = () => set('recipients', [...form.recipients, '']);
  const removeRecipient = (i) => set('recipients', form.recipients.filter((_, j) => j !== i));

  const submit = async () => {
    const recipients = (form.recipients || []).map((r) => r.trim()).filter(Boolean);
    if (!form.name.trim()) return toast.error('Nome regola obbligatorio');
    if (!recipients.length) return toast.error('Almeno un destinatario');
    if (!form.types?.length) return toast.error('Almeno un tipo di scadenza');
    if (!form.companyIds?.length) return toast.error('Almeno un\'azienda');
    setSaving(true);
    try {
      const payload = { ...form, recipients, advanceDays: Number(form.advanceDays), hourUtc: Number(form.hourUtc), dayOfWeek: Number(form.dayOfWeek), dayOfMonth: Number(form.dayOfMonth) };
      if (rule?.id) await notificationsApi.updateRule(rule.id, payload);
      else await notificationsApi.createRule(payload);
      toast.success(rule?.id ? 'Regola aggiornata' : 'Regola creata');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="rule-modal">
        <DialogHeader>
          <DialogTitle>{rule?.id ? 'Modifica regola di notifica' : 'Nuova regola di notifica'}</DialogTitle>
          <DialogDescription>
            Configura destinatari, tipi di scadenza, ambito aziendale e frequenza di invio.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Nome regola</label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="es. Promemoria settimanale 60 giorni" data-testid="rule-name" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Destinatari email</label>
            <div className="space-y-2">
              {form.recipients.map((r, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={r} onChange={(e) => updRecipient(i, e.target.value)} placeholder="email@example.com" type="email" data-testid={`recipient-${i}`} />
                  {form.recipients.length > 1 && (
                    <button onClick={() => removeRecipient(i)} className="px-2 rounded-md hover:bg-red-50 text-red-700"><X size={14} /></button>
                  )}
                </div>
              ))}
              <button onClick={addRecipient} className="ro-hide text-xs text-[#023F80] font-semibold hover:underline flex items-center gap-1"><Plus size={12} /> Aggiungi destinatario</button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Tipi di scadenza da segnalare</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(TYPE_LABELS).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => toggleType(k)}
                  data-testid={`type-${k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${form.types.includes(k) ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'}`}
                >{label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">Aziende</label>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <Checkbox checked={allCompanies} onCheckedChange={(v) => setAllCompanies(!!v)} data-testid="all-companies" />
              <span className="text-sm">Tutte le aziende</span>
            </label>
            {!allCompanies && (
              <div className="max-h-44 overflow-y-auto border rounded-lg p-2 space-y-1" data-testid="companies-multiselect">
                {companies.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox checked={form.companyIds.includes(c.id)} onCheckedChange={() => toggleCompany(c.id)} />
                    <span className="text-sm">{c.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Preavviso (giorni)</label>
              <Input type="number" min={1} max={365} value={form.advanceDays} onChange={(e) => set('advanceDays', e.target.value)} data-testid="advance-days" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Frequenza</label>
              <Select value={form.frequency} onValueChange={(v) => set('frequency', v)}>
                <SelectTrigger data-testid="frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FREQ_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Ora invio (UTC)</label>
              <Input type="number" min={0} max={23} value={form.hourUtc} onChange={(e) => set('hourUtc', e.target.value)} data-testid="hour-utc" />
            </div>
            {form.frequency === 'weekly' && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Giorno della settimana</label>
                <Select value={String(form.dayOfWeek)} onValueChange={(v) => set('dayOfWeek', Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOW.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.frequency === 'monthly' && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Giorno del mese (1–28)</label>
                <Input type="number" min={1} max={28} value={form.dayOfMonth} onChange={(e) => set('dayOfMonth', e.target.value)} />
              </div>
            )}
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={form.active} onCheckedChange={(v) => set('active', !!v)} data-testid="rule-active" />
                <span className="text-sm">Regola attiva</span>
              </label>
            </div>
          </div>

          {previewItems !== null && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs">
              <div className="font-semibold mb-1">Anteprima: {previewItems.length} scadenze corrispondono</div>
              {previewItems.slice(0, 5).map((it, i) => (
                <div key={i} className="text-slate-600">• {it.typeLabel} — <b>{it.name}</b> ({it.companyName}) — scade {it.scadenza}</div>
              ))}
              {previewItems.length > 5 && <div className="text-slate-400">… e altre {previewItems.length - 5}</div>}
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <button
            onClick={async () => {
              if (!rule?.id) return toast.info('Salva prima la regola per anteprima');
              const res = await notificationsApi.previewRule(rule.id);
              setPreviewItems(res.data.items);
            }}
            disabled={!rule?.id}
            className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold disabled:opacity-40"
          >Anteprima</button>
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold">Annulla</button>
          <button onClick={submit} disabled={saving} data-testid="save-rule" className="px-4 py-2 rounded-lg bg-[#023F80] text-white text-xs font-bold hover:bg-[#1e293b] flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin" />}
            Salva regola
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Export per ispezione tab
// ============================================================
const ExportCompanyTab = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const { data: companies, loading } = useFetch(() => companiesApi.list(), []);
  const [selectedId, setSelectedId] = useState(isClient ? user?.company : '');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (isClient && companies?.length && !selectedId) setSelectedId(companies[0].id);
  }, [isClient, companies, selectedId]);

  const selected = useMemo(() => (companies || []).find((c) => c.id === selectedId), [companies, selectedId]);

  const doDownload = async (type) => {
    if (!selectedId) return toast.error("Seleziona un'azienda");
    setDownloading(type);
    try {
      if (type === 'excel') await reportsApi.companyExcel(selectedId);
      else await reportsApi.companyZip(selectedId);
      toast.success('Download avviato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore download'); }
    finally { setDownloading(null); }
  };

  if (loading) return <Spinner />;

  return (
    <SectionCard
      icon={ShieldAlert}
      color="#b91c1c"
      bg="#fee2e2"
      title="Pacchetto Ispezione"
      subtitle="Scarica con un click tutte le scadenze e i file (attestati, certificati visite) di un'azienda — pronto per l'organo di vigilanza."
    >
      <div className="space-y-4">
        {!isClient && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Seleziona azienda</label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger data-testid="company-select" className="h-11"><SelectValue placeholder="Seleziona un'azienda..." /></SelectTrigger>
              <SelectContent className="max-h-80">
                {(companies || []).filter((c) => c.id).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {selected && (
          <div className="rounded-xl bg-gradient-to-r from-[#023F80] to-[#023F80] text-white p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <Building2 size={22} className="text-[#FFCB05]" />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-lg leading-tight truncate">{selected.name}</h3>
                <p className="text-xs text-white/70 mt-0.5 truncate">{selected.address}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            disabled={!selectedId || downloading}
            onClick={() => doDownload('excel')}
            data-testid="download-excel"
            className="rounded-xl border-2 border-[#023F80]/20 bg-white p-5 hover:border-[#023F80] hover:bg-[#e0e7ff]/30 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 text-green-700 flex items-center justify-center">
                <FileSpreadsheet size={20} />
              </div>
              <h4 className="font-bold text-gray-800">Excel scadenze</h4>
              {downloading === 'excel' && <Loader2 size={16} className="animate-spin text-slate-500 ml-auto" />}
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">File XLSX multi-foglio con Formazione, Visite, DPI, HACCP e Sicurezza. Ideale per archivio rapido.</p>
          </button>

          <button
            disabled={!selectedId || downloading}
            onClick={() => doDownload('zip')}
            data-testid="download-zip"
            className="rounded-xl border-2 border-[#023F80]/20 bg-white p-5 hover:border-[#023F80] hover:bg-[#e0e7ff]/30 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <FileArchive size={20} />
              </div>
              <h4 className="font-bold text-gray-800">ZIP completo (Excel + Allegati)</h4>
              {downloading === 'zip' && <Loader2 size={16} className="animate-spin text-slate-500 ml-auto" />}
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">Pacchetto archivio con Excel scadenze + cartella <code>attestati/</code> contenente tutti gli allegati caricati (PDF attestati, certificati idoneità medica).</p>
          </button>
        </div>
      </div>
    </SectionCard>
  );
};

// ============================================================
// Export Massivo tab
// ============================================================
const ExportBulkTab = () => {
  const [downloading, setDownloading] = useState(false);
  const doDownload = async () => {
    setDownloading(true);
    try {
      await reportsApi.bulkExcel();
      toast.success('Download avviato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore download');
    } finally { setDownloading(false); }
  };

  return (
    <SectionCard
      icon={FileSpreadsheet}
      color="#15803d"
      bg="#dcfce7"
      title="Export massivo scadenze"
      subtitle="Un singolo file Excel con TUTTE le scadenze di TUTTE le aziende. Perfetto per pianificare sessioni formative e visite mediche."
    >
      <div className="bg-gradient-to-br from-[#dcfce7] to-white border-2 border-green-200 rounded-xl p-6 text-center">
        <FileSpreadsheet size={48} className="mx-auto text-green-700 mb-3" />
        <h3 className="text-xl font-bold text-gray-800 mb-2">Scarica scadenze di tutte le aziende</h3>
        <p className="text-sm text-slate-600 mb-5 max-w-lg mx-auto">
          File Excel multi-foglio (Formazione, Visite, DPI, HACCP, Sicurezza) con tutte le scadenze ordinate per data. Ottimo per programmare le sessioni di rinnovo.
        </p>
        <button
          onClick={doDownload}
          disabled={downloading}
          data-testid="bulk-download"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#023F80] text-white font-bold hover:bg-[#1e293b] disabled:opacity-60"
        >
          {downloading ? <Loader2 size={18} className="animate-spin" /> : <FileSpreadsheet size={18} />}
          {downloading ? 'Generazione in corso...' : 'Scarica file Excel'}
        </button>
      </div>
    </SectionCard>
  );
};

export default ReportisticaPage;
